/**
 * useAnnouncementEvents Hook
 *
 * Fetches announcement events for the home page using URQL.
 */

import { useQuery } from "urql";
import type { Event } from "@/generated/graphql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// QUERY DOCUMENT
// =============================================================================

const AnnouncementEventsDocument = `
  query AnnouncementEvents {
    announcementEvents {
      id
      title
      description
      venue
      startsAt
      imageUrl
      bannerImageUrl
      ticketTypes {
        id
        name
        description
        price
        totalPrice
        currency
        maxQuantity
        soldCount
        available
      }
    }
  }
`;

// =============================================================================
// HOOK
// =============================================================================

interface UseAnnouncementEventsOptions {
  /** Whether to pause the query */
  pause?: boolean;
}

interface UseAnnouncementEventsResult {
  events: Event[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Fetch announcement events for home page carousel.
 *
 * Uses URQL for fetching featured/announcement events.
 */
export function useAnnouncementEvents(
  options: UseAnnouncementEventsOptions = {}
): UseAnnouncementEventsResult {
  const { pause = false } = options;

  const [result, reexecuteQuery] = useQuery<{ announcementEvents: Event[] }>({
    query: AnnouncementEventsDocument,
    pause,
  });

  // Track GraphQL errors
  if (result.error) {
    errorTracking.captureError(result.error, {
      extra: {
        graphqlErrors: result.error.graphQLErrors,
        networkError: result.error.networkError,
      },
    });
  }

  return {
    events: result.data?.announcementEvents ?? [],
    isLoading: result.fetching,
    error: result.error ?? null,
    refetch: () => reexecuteQuery({ requestPolicy: "network-only" }),
  };
}
